import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import mediaService from '../../services/mediaService';
import './MediaLibraryPage.css';

function MediaLibraryPage() {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (selectedFolder && selectedFolder !== 'all') params.folder = selectedFolder;

        const [mediaRes, foldersRes] = await Promise.all([
          mediaService.getMedia(params),
          mediaService.getFolders(),
        ]);

        setFiles(mediaRes.data.data);
        setFolders(foldersRes.data.data);
      } catch (err) {
        toast.error('Failed to load media library');
        console.error('Error fetching media:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [selectedFolder, searchTerm]);

  const fetchData = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedFolder && selectedFolder !== 'all') params.folder = selectedFolder;

      const [mediaRes, foldersRes] = await Promise.all([
        mediaService.getMedia(params),
        mediaService.getFolders(),
      ]);

      setFiles(mediaRes.data.data);
      setFolders(foldersRes.data.data);
    } catch (err) {
      toast.error('Failed to load media library');
      console.error('Error fetching media:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    if (uploadedFiles.length === 0) return;

    setIsUploading(true);

    try {
      for (const file of uploadedFiles) {
        await mediaService.uploadMedia(file, selectedFolder === 'all' ? 'products' : selectedFolder);
      }
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
      setShowUploadModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to upload file(s)');
      console.error('Error uploading:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }

    try {
      await mediaService.deleteMedia(file.id);
      toast.success('File deleted successfully');
      setShowDetailModal(false);
      setSelectedFile(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete file');
      console.error('Error deleting:', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.length} file(s)?`)) {
      return;
    }

    try {
      await mediaService.bulkDeleteMedia(selectedFiles);
      toast.success(`${selectedFiles.length} file(s) deleted`);
      setSelectedFiles([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete files');
      console.error('Error bulk deleting:', err);
    }
  };

  const handleSelectFile = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map((f) => f.id));
    }
  };

  const handleOpenDetail = (file) => {
    setSelectedFile(file);
    setShowDetailModal(true);
  };

  const handleUpdateAlt = async (newAlt) => {
    if (!selectedFile) return;

    try {
      await mediaService.updateMedia(selectedFile.id, { alt: newAlt });
      toast.success('Alt text updated');
      setSelectedFile((prev) => ({ ...prev, alt: newAlt }));
      fetchData();
    } catch (err) {
      toast.error('Failed to update alt text');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="media-library">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading media library...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="media-library">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Media Library</h1>
          <p>Manage your images and files.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Upload
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="media-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          {selectedFiles.length > 0 && (
            <button className="btn btn-danger-outline" onClick={handleBulkDelete}>
              Delete ({selectedFiles.length})
            </button>
          )}
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="media-content">
        {/* Sidebar */}
        <div className="media-sidebar">
          <h3>Folders</h3>
          <ul className="folder-list">
            {folders.map((folder) => (
              <li key={folder.id}>
                <button
                  className={`folder-item ${selectedFolder === folder.id ? 'active' : ''}`}
                  onClick={() => setSelectedFolder(folder.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                  </svg>
                  <span className="folder-name">{folder.name}</span>
                  <span className="folder-count">{folder.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Files Grid/List */}
        <div className="media-files">
          {files.length === 0 ? (
            <div className="empty-state">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              <h3>No files found</h3>
              <p>Upload your first file to get started.</p>
              <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
                Upload Files
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="files-grid">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`file-card ${selectedFiles.includes(file.id) ? 'selected' : ''}`}
                >
                  <div className="file-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => handleSelectFile(file.id)}
                    />
                  </div>
                  <div className="file-preview" onClick={() => handleOpenDetail(file)}>
                    {file.type === 'image' ? (
                      <img src={file.thumbnailUrl || file.url} alt={file.alt || file.name} />
                    ) : (
                      <div className="file-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="file-info">
                    <span className="file-name" title={file.name}>{file.name}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="files-list">
              <div className="list-header">
                <input
                  type="checkbox"
                  checked={selectedFiles.length === files.length && files.length > 0}
                  onChange={handleSelectAll}
                />
                <span className="col-name">Name</span>
                <span className="col-size">Size</span>
                <span className="col-date">Uploaded</span>
                <span className="col-actions"></span>
              </div>
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`list-row ${selectedFiles.includes(file.id) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file.id)}
                    onChange={() => handleSelectFile(file.id)}
                  />
                  <div className="col-name" onClick={() => handleOpenDetail(file)}>
                    {file.type === 'image' ? (
                      <img src={file.thumbnailUrl || file.url} alt={file.alt || file.name} className="list-thumb" />
                    ) : (
                      <div className="list-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                      </div>
                    )}
                    <span>{file.name}</span>
                  </div>
                  <span className="col-size">{formatFileSize(file.size)}</span>
                  <span className="col-date">{formatDate(file.createdAt)}</span>
                  <div className="col-actions">
                    <button className="btn-icon" onClick={() => handleOpenDetail(file)} title="View details">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    <button className="btn-icon danger" onClick={() => handleDelete(file)} title="Delete">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Files</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div
                className="upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <p>Click to upload or drag and drop</p>
                <span>PNG, JPG, PDF up to 10MB</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
              {isUploading && (
                <div className="upload-progress">
                  <div className="loading-spinner"></div>
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFile && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>File Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-preview">
                {selectedFile.type === 'image' ? (
                  <img src={selectedFile.url} alt={selectedFile.alt || selectedFile.name} />
                ) : (
                  <div className="detail-file-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="detail-info">
                <div className="detail-row">
                  <label>File Name</label>
                  <span>{selectedFile.name}</span>
                </div>
                <div className="detail-row">
                  <label>Size</label>
                  <span>{formatFileSize(selectedFile.size)}</span>
                </div>
                {selectedFile.width && selectedFile.height && (
                  <div className="detail-row">
                    <label>Dimensions</label>
                    <span>{selectedFile.width} x {selectedFile.height} px</span>
                  </div>
                )}
                <div className="detail-row">
                  <label>Folder</label>
                  <span className="folder-badge">{selectedFile.folder}</span>
                </div>
                <div className="detail-row">
                  <label>Uploaded</label>
                  <span>{formatDate(selectedFile.createdAt)} by {selectedFile.uploadedBy}</span>
                </div>
                <div className="detail-row">
                  <label>URL</label>
                  <div className="url-copy">
                    <input type="text" value={selectedFile.url} readOnly />
                    <button onClick={() => copyToClipboard(selectedFile.url)}>Copy</button>
                  </div>
                </div>
                {selectedFile.type === 'image' && (
                  <div className="detail-row">
                    <label>Alt Text</label>
                    <input
                      type="text"
                      value={selectedFile.alt || ''}
                      onChange={(e) => setSelectedFile((prev) => ({ ...prev, alt: e.target.value }))}
                      onBlur={(e) => handleUpdateAlt(e.target.value)}
                      placeholder="Add alt text for accessibility"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger-outline" onClick={() => handleDelete(selectedFile)}>
                Delete File
              </button>
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaLibraryPage;
